import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { useToast } from '../hooks/use-toast';
import { bookkeepingAPI, bankingAPI, rabobankAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  Calculator,
  Plus,
  Pencil,
  Trash2,
  TrendingUp,
  TrendingDown,
  Receipt,
  PiggyBank,
  BarChart3,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Lock,
  Tags,
  Palette,
  Building2,
  Upload,
  ShieldAlert,
  Paperclip,
  FileText,
  Eye,
  X,
  FileQuestion,
  FileCheck,
  FileMinus
} from 'lucide-react';

const EURO = '\u20AC';

const Bookkeeping = () => {
  const [activeTab, setActiveTab] = useState('transactions');
  const [transactions, setTransactions] = useState([]);
  const [btwData, setBtwData] = useState(null);
  const [profitLossData, setProfitLossData] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  
  // Categories
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    type: 'expense',
    color: '#6B7280'
  });
  
  // Banking
  const [bankingStatus, setBankingStatus] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [loadingBanking, setLoadingBanking] = useState(false);
  const [importingFile, setImportingFile] = useState(false);
  const [selectedBankType, setSelectedBankType] = useState('auto');
  
  // Attachments
  const [uploadingAttachment, setUploadingAttachment] = useState(null);
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  
  // Rabobank
  const [rabobankStatus, setRabobankStatus] = useState(null);
  const [rabobankTransactions, setRabobankTransactions] = useState([]);
  const [loadingRabobank, setLoadingRabobank] = useState(false);
  const [importingRabobank, setImportingRabobank] = useState(false);
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    category: 'expense',
    type: '',
    amount: '',
    btw_percentage: 21,
    reference: '',
    supplier: '',
    customer: '',
    paid: false,
    notes: ''
  });
  
  const { toast } = useToast();
  const { canCreate, canEdit, canDelete, user } = useAuth();
  
  // Check if user is owner
  const isOwner = user?.role === 'owner';

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const quarters = [1, 2, 3, 4];
  
  const colorOptions = [
    '#EF4444', '#F97316', '#F59E0B', '#84CC16', '#22C55E', 
    '#10B981', '#14B8A6', '#0EA5E9', '#3B82F6', '#6366F1',
    '#8B5CF6', '#A855F7', '#EC4899', '#F43F5E', '#64748B'
  ];

  useEffect(() => {
    if (isOwner) {
      fetchCategories();
      fetchData();
      fetchBankingData();
    }
  }, [isOwner]);
  
  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedQuarter]);
  
  const fetchCategories = async () => {
    try {
      const [expRes, incRes] = await Promise.all([
        bookkeepingAPI.getCategories('expense'),
        bookkeepingAPI.getCategories('income')
      ]);
      setExpenseCategories(expRes.data);
      setIncomeCategories(incRes.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };
  
  const fetchBankingData = async () => {
    try {
      setLoadingBanking(true);
      const [statusRes, importsRes, raboStatusRes] = await Promise.all([
        bankingAPI.getStatus(),
        bankingAPI.getImports(),
        rabobankAPI.getStatus().catch(() => ({ data: { configured: false, connected: false } }))
      ]);
      setBankingStatus(statusRes.data);
      setImportHistory(importsRes.data);
      setRabobankStatus(raboStatusRes.data);
    } catch (error) {
      console.error('Error fetching banking data:', error);
    } finally {
      setLoadingBanking(false);
    }
  };
  
  // Rabobank functions
  const connectRabobank = async () => {
    try {
      setLoadingRabobank(true);
      const redirectUri = `${window.location.origin}/bookkeeping?rabobank_callback=true`;
      const res = await rabobankAPI.authorize(redirectUri);
      
      // Store state for callback verification
      sessionStorage.setItem('rabobank_state', res.data.state);
      sessionStorage.setItem('rabobank_redirect_uri', redirectUri);
      
      // Redirect to Rabobank
      window.location.href = res.data.authorization_url;
    } catch (error) {
      console.error('Error connecting Rabobank:', error);
      toast({ title: "Fout", description: "Kon Rabobank niet koppelen", variant: "destructive" });
      setLoadingRabobank(false);
    }
  };
  
  const handleRabobankCallback = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    if (code && state) {
      try {
        setLoadingRabobank(true);
        const redirectUri = sessionStorage.getItem('rabobank_redirect_uri') || `${window.location.origin}/bookkeeping?rabobank_callback=true`;
        
        await rabobankAPI.callback(code, state, redirectUri);
        
        toast({ title: "Gelukt", description: "Rabobank succesvol gekoppeld!" });
        
        // Clean up URL and session storage
        window.history.replaceState({}, document.title, window.location.pathname);
        sessionStorage.removeItem('rabobank_state');
        sessionStorage.removeItem('rabobank_redirect_uri');
        
        // Refresh data
        fetchBankingData();
      } catch (error) {
        console.error('Error completing Rabobank callback:', error);
        toast({ title: "Fout", description: "Kon Rabobank koppeling niet voltooien", variant: "destructive" });
      } finally {
        setLoadingRabobank(false);
      }
    }
  };
  
  const fetchRabobankTransactions = async () => {
    try {
      setLoadingRabobank(true);
      const dateFrom = new Date();
      dateFrom.setDate(dateFrom.getDate() - 30);
      
      const res = await rabobankAPI.getTransactions(
        dateFrom.toISOString().split('T')[0],
        new Date().toISOString().split('T')[0]
      );
      
      setRabobankTransactions(res.data.transactions || []);
    } catch (error) {
      console.error('Error fetching Rabobank transactions:', error);
      toast({ title: "Fout", description: "Kon transacties niet ophalen", variant: "destructive" });
    } finally {
      setLoadingRabobank(false);
    }
  };
  
  const importRabobankTransactions = async () => {
    try {
      setImportingRabobank(true);
      const dateFrom = new Date();
      dateFrom.setDate(dateFrom.getDate() - 30);
      
      const res = await rabobankAPI.importTransactions(
        dateFrom.toISOString().split('T')[0],
        new Date().toISOString().split('T')[0]
      );
      
      toast({ title: "Gelukt", description: res.data.message });
      
      // Refresh bookkeeping data
      fetchData();
      fetchBankingData();
    } catch (error) {
      console.error('Error importing Rabobank transactions:', error);
      toast({ title: "Fout", description: "Kon transacties niet importeren", variant: "destructive" });
    } finally {
      setImportingRabobank(false);
    }
  };
  
  const disconnectRabobank = async () => {
    try {
      await rabobankAPI.disconnect();
      toast({ title: "Gelukt", description: "Rabobank koppeling verwijderd" });
      setRabobankStatus({ configured: true, connected: false });
      setRabobankTransactions([]);
    } catch (error) {
      toast({ title: "Fout", description: "Kon koppeling niet verwijderen", variant: "destructive" });
    }
  };
  
  // Check for Rabobank callback on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('rabobank_callback') || urlParams.get('code')) {
      handleRabobankCallback();
    }
  }, []);
  
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Check file type
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      toast({ title: "Fout", description: "Alleen CSV bestanden zijn toegestaan", variant: "destructive" });
      return;
    }
    
    try {
      setImportingFile(true);
      const res = await bankingAPI.importCSV(file, selectedBankType);
      
      toast({ 
        title: "Import Gelukt", 
        description: res.data.message 
      });
      
      // Refresh data
      fetchData();
      fetchBankingData();
      
      // Reset file input
      event.target.value = '';
    } catch (error) {
      console.error('Error importing file:', error);
      const msg = error.response?.data?.detail || "Kon bestand niet importeren";
      toast({ title: "Import Mislukt", description: msg, variant: "destructive" });
    } finally {
      setImportingFile(false);
    }
  };
  
  const deleteImportRecord = async (importId) => {
    try {
      await bankingAPI.deleteImport(importId);
      toast({ title: "Gelukt", description: "Import record verwijderd" });
      fetchBankingData();
    } catch (error) {
      toast({ title: "Fout", description: "Kon record niet verwijderen", variant: "destructive" });
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [transRes, summaryRes] = await Promise.all([
        bookkeepingAPI.getTransactions({ year: selectedYear }),
        bookkeepingAPI.getSummary(selectedYear)
      ]);
      setTransactions(transRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBtwData = async () => {
    try {
      const res = await bookkeepingAPI.getBtwDeclaration(selectedYear, selectedQuarter);
      setBtwData(res.data);
    } catch (error) {
      console.error('Error fetching BTW data:', error);
    }
  };

  const fetchProfitLoss = async () => {
    try {
      const res = await bookkeepingAPI.getProfitLoss(selectedYear);
      setProfitLossData(res.data);
    } catch (error) {
      console.error('Error fetching P&L:', error);
    }
  };

  const fetchBalance = async () => {
    try {
      const res = await bookkeepingAPI.getBalance(selectedYear);
      setBalanceData(res.data);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'btw') fetchBtwData();
    if (activeTab === 'profit-loss') fetchProfitLoss();
    if (activeTab === 'balance') fetchBalance();
  }, [activeTab, selectedYear, selectedQuarter]);

  const openCreateDialog = (category = 'expense') => {
    const defaultType = category === 'income' 
      ? (incomeCategories[0]?.name || 'Omzet verhuur')
      : (expenseCategories[0]?.name || 'Inkoop materialen');
    
    setEditingTransaction(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      description: '',
      category: category,
      type: defaultType,
      amount: '',
      btw_percentage: 21,
      reference: '',
      supplier: '',
      customer: '',
      paid: false,
      notes: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      date: transaction.date,
      description: transaction.description,
      category: transaction.category,
      type: transaction.type,
      amount: transaction.amount,
      btw_percentage: transaction.btw_percentage,
      reference: transaction.reference || '',
      supplier: transaction.supplier || '',
      customer: transaction.customer || '',
      paid: transaction.paid,
      notes: transaction.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.description || !formData.amount) {
      toast({ title: "Fout", description: "Vul alle verplichte velden in", variant: "destructive" });
      return;
    }

    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (editingTransaction) {
        await bookkeepingAPI.updateTransaction(editingTransaction.id, data);
        toast({ title: "Gelukt", description: "Transactie bijgewerkt" });
      } else {
        await bookkeepingAPI.createTransaction(data);
        toast({ title: "Gelukt", description: "Transactie toegevoegd" });
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: "Fout", description: "Kon transactie niet opslaan", variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    try {
      await bookkeepingAPI.deleteTransaction(id);
      toast({ title: "Gelukt", description: "Transactie verwijderd" });
      fetchData();
    } catch (error) {
      toast({ title: "Fout", description: "Kon transactie niet verwijderen", variant: "destructive" });
    }
  };
  
  // Category management
  const openCategoryDialog = (type = 'expense', category = null) => {
    setEditingCategory(category);
    if (category) {
      setCategoryForm({
        name: category.name,
        description: category.description || '',
        type: category.type,
        color: category.color || '#6B7280'
      });
    } else {
      setCategoryForm({
        name: '',
        description: '',
        type: type,
        color: '#6B7280'
      });
    }
    setCategoryDialogOpen(true);
  };
  
  const handleCategorySubmit = async () => {
    if (!categoryForm.name.trim()) {
      toast({ title: "Fout", description: "Vul een naam in", variant: "destructive" });
      return;
    }
    
    try {
      if (editingCategory) {
        await bookkeepingAPI.updateCategory(editingCategory.id, categoryForm);
        toast({ title: "Gelukt", description: "Categorie bijgewerkt" });
      } else {
        await bookkeepingAPI.createCategory(categoryForm);
        toast({ title: "Gelukt", description: "Categorie toegevoegd" });
      }
      setCategoryDialogOpen(false);
      fetchCategories();
    } catch (error) {
      const msg = error.response?.data?.detail || "Kon categorie niet opslaan";
      toast({ title: "Fout", description: msg, variant: "destructive" });
    }
  };
  
  const handleCategoryDelete = async (id, isDefault = false) => {
    try {
      await bookkeepingAPI.deleteCategory(id, isDefault);
      toast({ title: "Gelukt", description: "Categorie verwijderd" });
      fetchCategories();
    } catch (error) {
      const msg = error.response?.data?.detail || "Kon categorie niet verwijderen";
      toast({ title: "Fout", description: msg, variant: "destructive" });
    }
  };
  
  const cleanupDuplicates = async () => {
    try {
      const res = await bookkeepingAPI.cleanupDuplicateCategories();
      toast({ title: "Gelukt", description: res.data.message });
      fetchCategories();
    } catch (error) {
      toast({ title: "Fout", description: "Kon duplicaten niet opschonen", variant: "destructive" });
    }
  };
  
  // Attachment functions
  const handleAttachmentUpload = async (transactionId, file) => {
    try {
      setUploadingAttachment(transactionId);
      await bookkeepingAPI.uploadAttachment(transactionId, file);
      toast({ title: "Gelukt", description: "Factuur geüpload" });
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || "Kon factuur niet uploaden";
      toast({ title: "Fout", description: msg, variant: "destructive" });
    } finally {
      setUploadingAttachment(null);
    }
  };
  
  const handleAttachmentDelete = async (transactionId) => {
    try {
      await bookkeepingAPI.deleteAttachment(transactionId);
      toast({ title: "Gelukt", description: "Factuur verwijderd" });
      fetchData();
    } catch (error) {
      toast({ title: "Fout", description: "Kon factuur niet verwijderen", variant: "destructive" });
    }
  };
  
  const handleAttachmentStatusChange = async (transactionId, status) => {
    try {
      await bookkeepingAPI.updateAttachmentStatus(transactionId, status);
      toast({ title: "Gelukt", description: "Status bijgewerkt" });
      fetchData();
    } catch (error) {
      toast({ title: "Fout", description: "Kon status niet bijwerken", variant: "destructive" });
    }
  };
  
  const openAttachmentDialog = (transaction) => {
    setSelectedTransaction(transaction);
    setAttachmentDialogOpen(true);
  };
  
  const getAttachmentStatusIcon = (status) => {
    switch (status) {
      case 'uploaded': return <FileCheck className="w-4 h-4 text-green-600" />;
      case 'lost': return <FileQuestion className="w-4 h-4 text-orange-600" />;
      case 'not_needed': return <FileMinus className="w-4 h-4 text-gray-400" />;
      default: return <FileText className="w-4 h-4 text-gray-400" />;
    }
  };
  
  const getAttachmentStatusLabel = (status) => {
    switch (status) {
      case 'uploaded': return 'Geüpload';
      case 'lost': return 'Kwijt';
      case 'not_needed': return 'Niet nodig';
      default: return 'Geen';
    }
  };

  const formatCurrency = (amount) => {
    return `${EURO} ${(amount || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getTypeLabel = (type) => {
    const allCategories = [...expenseCategories, ...incomeCategories];
    return allCategories.find(c => c.name === type)?.name || type;
  };
  
  const getCategoryColor = (type) => {
    const allCategories = [...expenseCategories, ...incomeCategories];
    return allCategories.find(c => c.name === type)?.color || '#6B7280';
  };

  // Access check - must be after all hooks
  if (!isOwner) {
    return (
      <div className="p-8">
        <Card className="max-w-lg mx-auto shadow-lg border-0">
          <CardContent className="p-12 text-center">
            <ShieldAlert className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Toegang Geweigerd</h2>
            <p className="text-gray-600">
              De boekhouding is alleen toegankelijk voor de eigenaar.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Boekhouding laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Boekhouding</h1>
          <p className="text-gray-500 mt-1">Financieel overzicht, BTW aangifte en rapportages</p>
        </div>
        <div className="flex items-center space-x-3">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canCreate() ? (
            <div className="flex space-x-2">
              <Button onClick={() => openCreateDialog('income')} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Inkomst
              </Button>
              <Button onClick={() => openCreateDialog('expense')} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                <Plus className="w-4 h-4 mr-2" />
                Uitgave
              </Button>
            </div>
          ) : (
            <div className="flex items-center px-4 py-2 bg-gray-100 rounded-md text-gray-500">
              <Lock className="w-4 h-4 mr-2" />
              <span>Alleen lezen</span>
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="shadow-lg border-0 bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Inkomsten</p>
                  <p className="text-2xl font-bold text-green-700 mt-1">{formatCurrency(summary.totals.income)}</p>
                </div>
                <ArrowUpRight className="w-10 h-10 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0 bg-gradient-to-br from-red-50 to-orange-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600">Uitgaven</p>
                  <p className="text-2xl font-bold text-red-700 mt-1">{formatCurrency(summary.totals.expenses)}</p>
                </div>
                <ArrowDownRight className="w-10 h-10 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0 bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Winst</p>
                  <p className={`text-2xl font-bold mt-1 ${summary.totals.profit >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                    {formatCurrency(summary.totals.profit)}
                  </p>
                </div>
                <TrendingUp className="w-10 h-10 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0 bg-gradient-to-br from-purple-50 to-pink-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">BTW Saldo</p>
                  <p className={`text-2xl font-bold mt-1 ${summary.totals.btwBalance >= 0 ? 'text-purple-700' : 'text-green-700'}`}>
                    {formatCurrency(summary.totals.btwBalance)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {summary.totals.btwBalance >= 0 ? 'Te betalen' : 'Terug te vragen'}
                  </p>
                </div>
                <Calculator className="w-10 h-10 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6 lg:w-[900px]">
          <TabsTrigger value="transactions" className="flex items-center">
            <Receipt className="w-4 h-4 mr-2" />
            Transacties
          </TabsTrigger>
          <TabsTrigger value="btw" className="flex items-center">
            <Calculator className="w-4 h-4 mr-2" />
            BTW Aangifte
          </TabsTrigger>
          <TabsTrigger value="profit-loss" className="flex items-center">
            <BarChart3 className="w-4 h-4 mr-2" />
            Winst & Verlies
          </TabsTrigger>
          <TabsTrigger value="balance" className="flex items-center">
            <PiggyBank className="w-4 h-4 mr-2" />
            Balans
          </TabsTrigger>
          <TabsTrigger value="banking" className="flex items-center">
            <Building2 className="w-4 h-4 mr-2" />
            Bankrekening
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center">
            <Tags className="w-4 h-4 mr-2" />
            Categorieën
          </TabsTrigger>
        </TabsList>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="mt-6">
          <Card className="shadow-lg border-0">
            <CardHeader className="border-b bg-gradient-to-r from-gray-50 to-blue-50">
              <CardTitle className="flex items-center">
                <Receipt className="w-5 h-5 mr-2 text-blue-600" />
                Alle Transacties {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Datum</TableHead>
                    <TableHead>Omschrijving</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Referentie</TableHead>
                    <TableHead className="text-right">Bedrag excl.</TableHead>
                    <TableHead className="text-right">BTW</TableHead>
                    <TableHead className="text-right">Totaal</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Factuur</TableHead>
                    <TableHead>Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                        Geen transacties gevonden voor {selectedYear}
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((t) => (
                      <TableRow key={t.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{t.date}</TableCell>
                        <TableCell>{t.description}</TableCell>
                        <TableCell>
                          <Badge 
                            className="border-0"
                            style={{ 
                              backgroundColor: `${getCategoryColor(t.type)}20`,
                              color: getCategoryColor(t.type)
                            }}
                          >
                            {getTypeLabel(t.type)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-500">{t.reference || '-'}</TableCell>
                        <TableCell className={`text-right font-medium ${t.category === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                          {t.category === 'income' ? '+' : '-'}{formatCurrency(t.amount)}
                        </TableCell>
                        <TableCell className="text-right text-gray-600">{formatCurrency(t.btw_amount)}</TableCell>
                        <TableCell className={`text-right font-semibold ${t.category === 'income' ? 'text-green-700' : 'text-red-700'}`}>
                          {formatCurrency(t.total_amount)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={t.paid ? 'default' : 'outline'} className={t.paid ? 'bg-green-600' : ''}>
                            {t.paid ? 'Betaald' : 'Open'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className={t.attachmentStatus === 'uploaded' ? 'text-green-600' : 'text-gray-400'}
                            onClick={() => openAttachmentDialog(t)}
                          >
                            {uploadingAttachment === t.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                {getAttachmentStatusIcon(t.attachmentStatus)}
                                <span className="ml-1 text-xs hidden sm:inline">
                                  {t.attachmentStatus === 'uploaded' ? '1' : ''}
                                </span>
                              </>
                            )}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-1">
                            {canEdit() && (
                              <Button size="sm" variant="ghost" onClick={() => openEditDialog(t)}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                            {canDelete() && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="ghost" className="text-red-600">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Transactie Verwijderen</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Weet je zeker dat je deze transactie wilt verwijderen?
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Annuleren</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDelete(t.id)} className="bg-red-600">
                                      Verwijderen
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* BTW Declaration Tab */}
        <TabsContent value="btw" className="mt-6">
          <div className="flex items-center space-x-4 mb-4">
            <Label>Kwartaal:</Label>
            <Select value={selectedQuarter.toString()} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {quarters.map(q => (
                  <SelectItem key={q} value={q.toString()}>Q{q}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {btwData && (
            <Card className="shadow-lg border-0">
              <CardHeader className="border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                <CardTitle className="flex items-center">
                  <Calculator className="w-5 h-5 mr-2" />
                  BTW Aangifte - {btwData.period}
                </CardTitle>
                <p className="text-blue-100 text-sm">{btwData.startDate} t/m {btwData.endDate}</p>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-6">
                  {/* Section 1: Prestaties binnenland */}
                  <div>
                    <h3 className="font-bold text-lg mb-3 text-blue-800">1. Prestaties binnenland</h3>
                    <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                      <div>
                        <Label className="text-xs text-gray-500">1a. Leveringen/diensten belast met hoog tarief</Label>
                        <div className="flex justify-between mt-1">
                          <span className="font-medium">{formatCurrency(btwData['1a_amount'])}</span>
                          <span className="text-blue-600 font-bold">{formatCurrency(btwData['1a_btw'])}</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">1b. Leveringen/diensten belast met laag tarief</Label>
                        <div className="flex justify-between mt-1">
                          <span className="font-medium">{formatCurrency(btwData['1b_amount'])}</span>
                          <span className="text-blue-600 font-bold">{formatCurrency(btwData['1b_btw'])}</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">1c. Leveringen/diensten belast met overige tarieven</Label>
                        <div className="flex justify-between mt-1">
                          <span className="font-medium">{formatCurrency(btwData['1c_amount'])}</span>
                          <span className="text-gray-400">-</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 5: Voorbelasting */}
                  <div>
                    <h3 className="font-bold text-lg mb-3 text-blue-800">5. Voorbelasting, kleineondernemersregeling en eindtotaal</h3>
                    <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <Label>5a. Verschuldigde omzetbelasting (rubrieken 1 t/m 4)</Label>
                        <span className="text-xl font-bold text-blue-700">{formatCurrency(btwData['5a_btw'])}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <Label>5b. Voorbelasting</Label>
                        <span className="text-xl font-bold text-green-600">- {formatCurrency(btwData['5b_btw'])}</span>
                      </div>
                      <div className="border-t pt-3 flex justify-between items-center">
                        <Label className="text-lg">5c. Subtotaal (rubriek 5a min 5b)</Label>
                        <span className={`text-2xl font-bold ${btwData['5c_btw'] >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(Math.abs(btwData['5c_btw']))}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Summary Box */}
                  <div className={`p-6 rounded-lg ${btwData.btwToPay > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-bold text-lg">
                          {btwData.btwToPay > 0 ? 'Te betalen aan Belastingdienst' : 'Terug te vragen van Belastingdienst'}
                        </h4>
                        <p className="text-sm text-gray-600">
                          Omzet: {formatCurrency(btwData.totalIncome)} | Kosten: {formatCurrency(btwData.totalExpenses)}
                        </p>
                      </div>
                      <span className={`text-3xl font-bold ${btwData.btwToPay > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(btwData.btwToPay > 0 ? btwData.btwToPay : btwData.btwToReceive)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Profit & Loss Tab */}
        <TabsContent value="profit-loss" className="mt-6">
          {profitLossData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="shadow-lg border-0">
                <CardHeader className="border-b bg-gradient-to-r from-green-50 to-emerald-50">
                  <CardTitle className="flex items-center text-green-700">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Inkomsten {selectedYear}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Omzet uit facturen</span>
                      <span className="font-medium">{formatCurrency(profitLossData.income.invoices)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Overige inkomsten</span>
                      <span className="font-medium">{formatCurrency(profitLossData.income.transactions)}</span>
                    </div>
                    <div className="border-t pt-3 flex justify-between">
                      <span className="font-bold">Totaal Inkomsten</span>
                      <span className="text-xl font-bold text-green-600">{formatCurrency(profitLossData.income.total)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-0">
                <CardHeader className="border-b bg-gradient-to-r from-red-50 to-orange-50">
                  <CardTitle className="flex items-center text-red-700">
                    <TrendingDown className="w-5 h-5 mr-2" />
                    Uitgaven {selectedYear}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-3">
                    {Object.entries(profitLossData.expenses.byType).map(([type, amount]) => (
                      <div key={type} className="flex justify-between">
                        <span>{getTypeLabel(type)}</span>
                        <span className="font-medium">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                    {Object.keys(profitLossData.expenses.byType).length === 0 && (
                      <p className="text-gray-500 text-center py-4">Geen uitgaven geregistreerd</p>
                    )}
                    <div className="border-t pt-3 flex justify-between">
                      <span className="font-bold">Totaal Uitgaven</span>
                      <span className="text-xl font-bold text-red-600">{formatCurrency(profitLossData.expenses.total)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-0 lg:col-span-2">
                <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                  <CardTitle className="flex items-center text-blue-700">
                    <BarChart3 className="w-5 h-5 mr-2" />
                    Resultaat per Kwartaal
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-4 gap-4">
                    {profitLossData.quarterly.map((q) => (
                      <div key={q.quarter} className="text-center p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-bold text-lg">{q.quarter}</h4>
                        <p className="text-sm text-green-600">+{formatCurrency(q.income)}</p>
                        <p className="text-sm text-red-600">-{formatCurrency(q.expenses)}</p>
                        <p className={`font-bold mt-2 ${q.profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          {formatCurrency(q.profit)}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className={`mt-6 p-6 rounded-lg ${profitLossData.grossProfit >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold">Bruto Winst {selectedYear}</span>
                      <span className={`text-3xl font-bold ${profitLossData.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(profitLossData.grossProfit)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Balance Tab */}
        <TabsContent value="balance" className="mt-6">
          {balanceData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="shadow-lg border-0">
                <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-cyan-50">
                  <CardTitle className="flex items-center text-blue-700">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Activa (Bezittingen)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <span>Apparatuur & Inventaris</span>
                      <span className="font-medium">{formatCurrency(balanceData.assets.equipment)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <span>Debiteuren (Openstaande facturen)</span>
                      <span className="font-medium">{formatCurrency(balanceData.assets.receivables)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <span>Liquide middelen (Kas/Bank)</span>
                      <span className="font-medium">{formatCurrency(balanceData.assets.cash)}</span>
                    </div>
                    <div className="border-t pt-4 flex justify-between items-center">
                      <span className="font-bold text-lg">Totaal Activa</span>
                      <span className="text-2xl font-bold text-blue-600">{formatCurrency(balanceData.assets.total)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-0">
                <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50">
                  <CardTitle className="flex items-center text-purple-700">
                    <TrendingDown className="w-5 h-5 mr-2" />
                    Passiva (Schulden & Eigen Vermogen)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-600">Schulden</h4>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <span>BTW schuld</span>
                      <span className="font-medium">{formatCurrency(balanceData.liabilities.btwPayable)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <span>Crediteuren (Openstaande rekeningen)</span>
                      <span className="font-medium">{formatCurrency(balanceData.liabilities.creditors)}</span>
                    </div>
                    
                    <h4 className="font-semibold text-gray-600 pt-4">Eigen Vermogen</h4>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                      <span>Eigen vermogen</span>
                      <span className="font-bold text-green-600">{formatCurrency(balanceData.equity)}</span>
                    </div>
                    
                    <div className="border-t pt-4 flex justify-between items-center">
                      <span className="font-bold text-lg">Totaal Passiva</span>
                      <span className="text-2xl font-bold text-purple-600">{formatCurrency(balanceData.totalLiabilitiesAndEquity)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
        
        {/* Banking Tab */}
        <TabsContent value="banking" className="mt-6">
          <div className="space-y-6">
            {/* Rabobank Boekhoudkoppeling */}
            <Card className="shadow-lg border-0">
              <CardHeader className="border-b bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center">
                    <Building2 className="w-5 h-5 mr-2" />
                    Rabobank Boekhoudkoppeling
                  </CardTitle>
                  {rabobankStatus?.connected && (
                    <Badge className="bg-white text-orange-600">Gekoppeld</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {!rabobankStatus?.configured ? (
                  <div className="text-center py-4">
                    <p className="text-gray-600 mb-2">Rabobank API niet geconfigureerd</p>
                    <p className="text-xs text-gray-400">Voeg RABOBANK_CLIENT_ID en RABOBANK_CLIENT_SECRET toe aan backend/.env</p>
                  </div>
                ) : rabobankStatus?.connected ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">Rabobank</p>
                          <p className="text-sm text-gray-600">{rabobankStatus.iban || 'Rekening gekoppeld'}</p>
                          {rabobankStatus.last_sync && (
                            <p className="text-xs text-gray-400">
                              Laatste sync: {new Date(rabobankStatus.last_sync).toLocaleString('nl-NL')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={fetchRabobankTransactions}
                          disabled={loadingRabobank}
                        >
                          {loadingRabobank ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Bekijk transacties'}
                        </Button>
                        <Button
                          size="sm"
                          onClick={importRabobankTransactions}
                          disabled={importingRabobank}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {importingRabobank ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Importeer'}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Rabobank Ontkoppelen</AlertDialogTitle>
                              <AlertDialogDescription>
                                Weet je zeker dat je de Rabobank koppeling wilt verwijderen?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuleren</AlertDialogCancel>
                              <AlertDialogAction onClick={disconnectRabobank} className="bg-red-600">
                                Ontkoppelen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                    
                    {/* Rabobank Transactions Preview */}
                    {rabobankTransactions.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50">
                              <TableHead>Datum</TableHead>
                              <TableHead>Omschrijving</TableHead>
                              <TableHead>Tegenpartij</TableHead>
                              <TableHead className="text-right">Bedrag</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {rabobankTransactions.slice(0, 10).map((txn, idx) => (
                              <TableRow key={txn.id || idx}>
                                <TableCell>{txn.date}</TableCell>
                                <TableCell className="max-w-xs truncate">{txn.description}</TableCell>
                                <TableCell>{txn.counterparty || '-'}</TableCell>
                                <TableCell className={`text-right font-medium ${txn.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {txn.amount >= 0 ? '+' : ''}{formatCurrency(txn.amount)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {rabobankTransactions.length > 10 && (
                          <div className="p-2 text-center text-sm text-gray-500 bg-gray-50">
                            En {rabobankTransactions.length - 10} meer transacties...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Building2 className="w-12 h-12 text-orange-400 mx-auto mb-3" />
                    <h4 className="font-semibold mb-2">Koppel uw Rabobank rekening</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Automatisch transacties ophalen via de Rabobank Boekhoudkoppeling API
                    </p>
                    <Button 
                      onClick={connectRabobank}
                      disabled={loadingRabobank}
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      {loadingRabobank ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Building2 className="w-4 h-4 mr-2" />
                      )}
                      Rabobank Koppelen
                    </Button>
                    {rabobankStatus?.sandbox && (
                      <p className="text-xs text-orange-600 mt-2">Sandbox modus actief</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* CSV Import Card */}
            <Card className="shadow-lg border-0">
              <CardHeader className="border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center">
                    <Building2 className="w-5 h-5 mr-2" />
                    CSV Import (Alle banken)
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-6">
                  {/* Instructions */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Hoe werkt het?</h4>
                    <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                      <li>Log in op uw internetbankieren (ING, ABN AMRO, Rabobank, etc.)</li>
                      <li>Exporteer uw transacties als CSV bestand</li>
                      <li>Upload het bestand hieronder</li>
                      <li>Transacties worden automatisch gecategoriseerd en geïmporteerd</li>
                    </ol>
                  </div>
                  
                  {/* Bank Selection and Upload */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Bank (optioneel)</Label>
                      <Select value={selectedBankType} onValueChange={setSelectedBankType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Automatisch detecteren" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Automatisch detecteren</SelectItem>
                          <SelectItem value="ing">ING</SelectItem>
                          <SelectItem value="abn_amro">ABN AMRO</SelectItem>
                          <SelectItem value="rabobank">Rabobank</SelectItem>
                          <SelectItem value="generic">Generiek CSV</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">
                        Laat op "Automatisch" staan, het systeem detecteert uw bank automatisch
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>CSV Bestand uploaden</Label>
                      <div className="flex items-center space-x-2">
                        <Input
                          type="file"
                          accept=".csv,.txt"
                          onChange={handleFileUpload}
                          disabled={importingFile}
                          className="flex-1"
                        />
                        {importingFile && (
                          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Supported Banks */}
                  {bankingStatus?.supported_banks && (
                    <div className="pt-4 border-t">
                      <p className="text-sm text-gray-600 mb-2">Ondersteunde banken:</p>
                      <div className="flex flex-wrap gap-2">
                        {bankingStatus.supported_banks.map((bank) => (
                          <Badge key={bank} variant="outline" className="bg-white">
                            {bank}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Import History */}
            {importHistory.length > 0 && (
              <Card className="shadow-lg border-0">
                <CardHeader className="border-b bg-gradient-to-r from-gray-50 to-blue-50">
                  <CardTitle className="flex items-center">
                    <Receipt className="w-5 h-5 mr-2 text-blue-600" />
                    Import Geschiedenis
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Datum</TableHead>
                        <TableHead>Bestand</TableHead>
                        <TableHead>Bank</TableHead>
                        <TableHead>Totaal</TableHead>
                        <TableHead>Geïmporteerd</TableHead>
                        <TableHead>Overgeslagen</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importHistory.map((imp) => (
                        <TableRow key={imp.id}>
                          <TableCell className="font-medium">
                            {new Date(imp.created_at).toLocaleDateString('nl-NL')}
                          </TableCell>
                          <TableCell>{imp.filename}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {imp.bank_type === 'ing' ? 'ING' : 
                               imp.bank_type === 'abn_amro' ? 'ABN AMRO' : 
                               imp.bank_type === 'rabobank' ? 'Rabobank' : 
                               imp.bank_type.charAt(0).toUpperCase() + imp.bank_type.slice(1)}
                            </Badge>
                          </TableCell>
                          <TableCell>{imp.transaction_count}</TableCell>
                          <TableCell className="text-green-600 font-medium">{imp.imported_count}</TableCell>
                          <TableCell className="text-gray-500">{imp.skipped_count}</TableCell>
                          <TableCell>
                            {canDelete() && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-600"
                                onClick={() => deleteImportRecord(imp.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
        
        {/* Categories Tab */}
        <TabsContent value="categories" className="mt-6">
          <div className="space-y-6">
            {/* Cleanup duplicates button */}
            <div className="flex justify-end">
              <Button 
                variant="outline" 
                onClick={cleanupDuplicates}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Duplicaten Opschonen
              </Button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Expense Categories */}
            <Card className="shadow-lg border-0">
              <CardHeader className="border-b bg-gradient-to-r from-red-50 to-orange-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center text-red-700">
                    <TrendingDown className="w-5 h-5 mr-2" />
                    Uitgave Categorieën
                  </CardTitle>
                  {canCreate() && (
                    <Button size="sm" onClick={() => openCategoryDialog('expense')} className="bg-red-600 hover:bg-red-700">
                      <Plus className="w-4 h-4 mr-1" />
                      Nieuw
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2">
                  {expenseCategories.map((cat) => (
                    <div key={cat.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: cat.color }}
                        />
                        <div>
                          <p className="font-medium">{cat.name}</p>
                          {cat.description && (
                            <p className="text-xs text-gray-500">{cat.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        {cat.isDefault && (
                          <Badge variant="outline" className="text-xs mr-2">Standaard</Badge>
                        )}
                        {canEdit() && (
                          <Button size="sm" variant="ghost" onClick={() => openCategoryDialog('expense', cat)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete() && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Categorie Verwijderen</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Weet je zeker dat je "{cat.name}" wilt verwijderen?
                                  {cat.isDefault && " Dit is een standaard categorie."}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleCategoryDelete(cat.id, cat.isDefault)} className="bg-red-600">
                                  Verwijderen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Income Categories */}
            <Card className="shadow-lg border-0">
              <CardHeader className="border-b bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center text-green-700">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Inkomsten Categorieën
                  </CardTitle>
                  {canCreate() && (
                    <Button size="sm" onClick={() => openCategoryDialog('income')} className="bg-green-600 hover:bg-green-700">
                      <Plus className="w-4 h-4 mr-1" />
                      Nieuw
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2">
                  {incomeCategories.map((cat) => (
                    <div key={cat.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: cat.color }}
                        />
                        <div>
                          <p className="font-medium">{cat.name}</p>
                          {cat.description && (
                            <p className="text-xs text-gray-500">{cat.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        {cat.isDefault && (
                          <Badge variant="outline" className="text-xs mr-2">Standaard</Badge>
                        )}
                        {canEdit() && (
                          <Button size="sm" variant="ghost" onClick={() => openCategoryDialog('income', cat)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete() && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Categorie Verwijderen</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Weet je zeker dat je "{cat.name}" wilt verwijderen?
                                  {cat.isDefault && " Dit is een standaard categorie."}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleCategoryDelete(cat.id, cat.isDefault)} className="bg-red-600">
                                  Verwijderen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Transaction Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              {formData.category === 'income' ? (
                <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
              ) : (
                <TrendingDown className="w-5 h-5 mr-2 text-red-600" />
              )}
              {editingTransaction ? 'Transactie Bewerken' : formData.category === 'income' ? 'Nieuwe Inkomst' : 'Nieuwe Uitgave'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Datum *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Categorie *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(v) => setFormData({ ...formData, type: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer categorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {(formData.category === 'income' ? incomeCategories : expenseCategories).map(cat => (
                      <SelectItem key={cat.id} value={cat.name}>
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-2" 
                            style={{ backgroundColor: cat.color }}
                          />
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Omschrijving *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Bijv. Huur speaker set"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bedrag excl. BTW *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>BTW %</Label>
                <Select
                  value={formData.btw_percentage.toString()}
                  onValueChange={(v) => setFormData({ ...formData, btw_percentage: parseFloat(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="21">21% (Hoog tarief)</SelectItem>
                    <SelectItem value="9">9% (Laag tarief)</SelectItem>
                    <SelectItem value="0">0% (Vrijgesteld)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.amount && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span>BTW ({formData.btw_percentage}%)</span>
                  <span>{formatCurrency(parseFloat(formData.amount || 0) * (formData.btw_percentage / 100))}</span>
                </div>
                <div className="flex justify-between font-bold mt-1">
                  <span>Totaal incl. BTW</span>
                  <span>{formatCurrency(parseFloat(formData.amount || 0) * (1 + formData.btw_percentage / 100))}</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Referentie/Factuurnr.</Label>
                <Input
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  placeholder="Bijv. F-2024-001"
                />
              </div>
              <div className="space-y-2">
                <Label>{formData.category === 'income' ? 'Klant' : 'Leverancier'}</Label>
                <Input
                  value={formData.category === 'income' ? formData.customer : formData.supplier}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    [formData.category === 'income' ? 'customer' : 'supplier']: e.target.value 
                  })}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="paid"
                checked={formData.paid}
                onChange={(e) => setFormData({ ...formData, paid: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="paid">Betaald</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuleren</Button>
            <Button onClick={handleSubmit} className={formData.category === 'income' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}>
              {editingTransaction ? 'Bijwerken' : 'Toevoegen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add/Edit Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Tags className="w-5 h-5 mr-2" />
              {editingCategory ? 'Categorie Bewerken' : 'Nieuwe Categorie'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Naam *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="Bijv. Abonnementen"
                disabled={editingCategory?.isDefault}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Omschrijving</Label>
              <Input
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Korte beschrijving"
              />
            </div>
            
            {!editingCategory && (
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={categoryForm.type}
                  onValueChange={(v) => setCategoryForm({ ...categoryForm, type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expense">Uitgave</SelectItem>
                    <SelectItem value="income">Inkomst</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="flex items-center">
                <Palette className="w-4 h-4 mr-2" />
                Kleur
              </Label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                      categoryForm.color === color ? 'border-gray-900 scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setCategoryForm({ ...categoryForm, color })}
                  />
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>Annuleren</Button>
            <Button onClick={handleCategorySubmit} className={categoryForm.type === 'income' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}>
              {editingCategory ? 'Bijwerken' : 'Toevoegen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Category Dialog only - Bank dialog removed */}
      
      {/* Attachment Dialog */}
      <Dialog open={attachmentDialogOpen} onOpenChange={setAttachmentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Paperclip className="w-5 h-5 mr-2" />
              Factuur Beheren
            </DialogTitle>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="space-y-4">
              {/* Transaction Info */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium">{selectedTransaction.description}</p>
                <p className="text-sm text-gray-600">
                  {selectedTransaction.date} - {formatCurrency(selectedTransaction.total_amount)}
                </p>
              </div>
              
              {/* Current Attachment */}
              {selectedTransaction.attachmentStatus === 'uploaded' && selectedTransaction.attachmentUrl && (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <FileCheck className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-700">{selectedTransaction.attachment || 'Factuur'}</p>
                      <p className="text-xs text-green-600">Geüpload</p>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => window.open(process.env.REACT_APP_BACKEND_URL + selectedTransaction.attachmentUrl, '_blank')}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-red-600"
                      onClick={() => {
                        handleAttachmentDelete(selectedTransaction.id);
                        setAttachmentDialogOpen(false);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Upload Area */}
              {selectedTransaction.attachmentStatus !== 'uploaded' && (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                  <Paperclip className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">Sleep hier de bijbehorende factuur</p>
                  <p className="text-xs text-gray-400 mb-3">PDF, PNG, JPG (max 10MB)</p>
                  <Input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleAttachmentUpload(selectedTransaction.id, file);
                        setAttachmentDialogOpen(false);
                      }
                    }}
                    className="max-w-xs mx-auto"
                  />
                </div>
              )}
              
              {/* Status Buttons */}
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600 mb-2">Of markeer als:</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={selectedTransaction.attachmentStatus === 'not_needed' ? 'default' : 'outline'}
                    className={selectedTransaction.attachmentStatus === 'not_needed' ? 'bg-gray-600' : ''}
                    onClick={() => {
                      handleAttachmentStatusChange(selectedTransaction.id, 'not_needed');
                      setAttachmentDialogOpen(false);
                    }}
                  >
                    <FileMinus className="w-4 h-4 mr-1" />
                    Niet nodig
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedTransaction.attachmentStatus === 'lost' ? 'default' : 'outline'}
                    className={selectedTransaction.attachmentStatus === 'lost' ? 'bg-orange-600' : ''}
                    onClick={() => {
                      handleAttachmentStatusChange(selectedTransaction.id, 'lost');
                      setAttachmentDialogOpen(false);
                    }}
                  >
                    <FileQuestion className="w-4 h-4 mr-1" />
                    Kwijt
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachmentDialogOpen(false)}>Sluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Bookkeeping;
